import { useRef, useState } from "react";
import { FileText, Upload, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../api";
import { resolveImage } from "../../lib/data";

/**
 * PDF uploader for investor documents.
 * Validates: type=application/pdf, max 4 MB (server enforces too).
 * On upload, populates `value` (relative /api/files/...) and reports
 * file size in bytes via `onSizeChange` so the form can show "3.2 MB".
 */
export default function PdfUploader({ value, onChange, onSizeChange, testId }) {
  const [uploading, setUploading] = useState(false);
  const [filename, setFilename] = useState("");
  const inputRef = useRef(null);

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Sono accettati solo file PDF");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Il PDF supera i 4 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await adminApi.post("/admin/upload-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(r.data.url);
      onSizeChange?.(r.data.size, r.data.filename);
      setFilename(r.data.filename || file.name);
      toast.success("PDF caricato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Caricamento fallito");
    } finally {
      setUploading(false);
    }
  };

  const clear = () => {
    onChange?.("");
    onSizeChange?.(0, "");
    setFilename("");
  };

  return (
    <div data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={onFile}
      />
      {value ? (
        <div className="flex items-center gap-3 p-3 rounded-lg border border-white/10 bg-black/30">
          <div className="h-10 w-10 rounded-md bg-[#C48F56]/15 grid place-items-center text-[#C48F56] shrink-0">
            <FileText size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate">{filename || "Documento PDF"}</div>
            <a
              href={resolveImage(value)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-white/40 hover:text-[#C48F56] uppercase tracking-[0.2em] font-mono"
            >
              Apri PDF <ExternalLink size={10} />
            </a>
          </div>
          <button
            type="button"
            onClick={pick}
            disabled={uploading}
            className="text-xs uppercase tracking-[0.2em] font-mono text-white/60 hover:text-white"
            title="Sostituisci"
          >
            Sostituisci
          </button>
          <button
            type="button"
            onClick={clear}
            className="h-9 w-9 grid place-items-center rounded-full hover:bg-red-500/10 hover:text-red-400 text-white/50"
            title="Rimuovi"
          >
            <Trash2 size={13} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={pick}
          disabled={uploading}
          className="w-full flex items-center gap-3 p-4 rounded-lg border border-dashed border-white/15 hover:border-[#C48F56]/50 hover:bg-white/[0.02] transition-colors text-left"
        >
          <div className="h-10 w-10 rounded-md bg-white/5 grid place-items-center text-white/50">
            <Upload size={16} />
          </div>
          <div className="flex-1">
            <div className="text-sm text-white/80">
              {uploading ? "Caricamento…" : "Carica PDF"}
            </div>
            <div className="text-[11px] text-white/40 mt-0.5">
              Massimo 4 MB · solo formato PDF
            </div>
          </div>
        </button>
      )}
    </div>
  );
}

/** Format bytes into "1.2 MB" Italian style. */
export function formatBytes(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
}
